// Modern JavaScript utilities to replace underscore/lodash functions

// Map function - equivalent to _.map
export const map = (collection, iteratee) => {
    if (!collection) return [];
    if (Array.isArray(collection)) {
        return collection.map(iteratee);
    }
    // Handle array-like objects (like jQuery collections)
    return Array.from(collection).map(iteratee);
};

// Each function - equivalent to _.each
export const each = (collection, iteratee) => {
    if (!collection) return;
    if (Array.isArray(collection)) {
        collection.forEach(iteratee);
    } else if (typeof collection === 'object') {
        Object.keys(collection).forEach((key, index) => {
            iteratee(collection[key], key, index);
        });
    } else {
        // Handle array-like objects (like jQuery collections)
        Array.from(collection).forEach(iteratee);
    }
};

// Delay function - equivalent to _.delay
export const delay = (func, wait, ...args) => {
    return setTimeout(() => func.apply(null, args), wait);
};

// Debounce function - equivalent to _.debounce
export const debounce = (func, wait, options = {}) => {
    let timeoutId;
    let lastArgs;
    let lastThis;
    let result;
    let lastCallTime;
    let lastInvokeTime = 0;
    let leading = !!options.leading;
    let trailing = options.trailing !== false;

    function invokeFunc(time) {
        const args = lastArgs;
        const thisArg = lastThis;

        lastArgs = lastThis = undefined;
        lastInvokeTime = time;
        result = func.apply(thisArg, args);
        return result;
    }

    function startTimer(pendingFunc, wait) {
        return setTimeout(pendingFunc, wait);
    }

    function cancelTimer(id) {
        clearTimeout(id);
    }

    function leadingEdge(time) {
        lastInvokeTime = time;
        timeoutId = startTimer(timerExpired, wait);
        return leading ? invokeFunc(time) : result;
    }

    function remainingWait(time) {
        const timeSinceLastCall = time - lastCallTime;
        const timeSinceLastInvoke = time - lastInvokeTime;
        const timeWaiting = wait - timeSinceLastCall;

        return timeWaiting;
    }

    function shouldInvoke(time) {
        const timeSinceLastCall = time - lastCallTime;
        const timeSinceLastInvoke = time - lastInvokeTime;

        return (lastCallTime === undefined || (timeSinceLastCall >= wait) ||
                (timeSinceLastCall < 0) || (timeSinceLastInvoke >= wait));
    }

    function timerExpired() {
        const time = Date.now();
        if (shouldInvoke(time)) {
            return trailingEdge(time);
        }
        timeoutId = startTimer(timerExpired, remainingWait(time));
    }

    function trailingEdge(time) {
        timeoutId = undefined;

        if (trailing && lastArgs) {
            return invokeFunc(time);
        }
        lastArgs = lastThis = undefined;
        return result;
    }

    function cancel() {
        if (timeoutId !== undefined) {
            cancelTimer(timeoutId);
        }
        lastInvokeTime = 0;
        lastArgs = lastCallTime = lastThis = timeoutId = undefined;
    }

    function flush() {
        return timeoutId === undefined ? result : trailingEdge(Date.now());
    }

    function pending() {
        return timeoutId !== undefined;
    }

    function debounced(...args) {
        const time = Date.now();
        const isInvoking = shouldInvoke(time);

        lastArgs = args;
        lastThis = this;
        lastCallTime = time;

        if (isInvoking) {
            if (timeoutId === undefined) {
                return leadingEdge(lastCallTime);
            }
            if (trailing) {
                timeoutId = startTimer(timerExpired, wait);
                return invokeFunc(lastCallTime);
            }
        }
        if (timeoutId === undefined) {
            timeoutId = startTimer(timerExpired, wait);
        }
        return result;
    }

    debounced.cancel = cancel;
    debounced.flush = flush;
    debounced.pending = pending;
    return debounced;
};

// Throttle function - equivalent to _.throttle
export const throttle = (func, wait, options = {}) => {
    let leading = true;
    let trailing = true;

    if (typeof func !== 'function') {
        throw new TypeError('Expected a function');
    }
    if (options.leading !== undefined) {
        leading = !!options.leading;
    }
    if (options.trailing !== undefined) {
        trailing = !!options.trailing;
    }

    return debounce(func, wait, {
        'leading': leading,
        'maxWait': wait,
        'trailing': trailing
    });
};

// Pluck function - equivalent to _.pluck (deprecated in lodash, use map instead)
export const pluck = (collection, key) => {
    return map(collection, item => item[key]);
};

// FindWhere function - equivalent to _.findWhere (deprecated in lodash, use find instead)
export const findWhere = (collection, properties) => {
    if (!collection) return undefined;
    if (Array.isArray(collection)) {
        return collection.find(item => {
            return Object.keys(properties).every(key => item[key] === properties[key]);
        });
    }
    return undefined;
};

// Without function - equivalent to _.without
export const without = (array, ...values) => {
    if (!array) return [];
    return array.filter(item => !values.includes(item));
};

// Chunk function - equivalent to _.chunk
export const chunk = (array, size) => {
    if (!array || size <= 0) return [];
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
        chunks.push(array.slice(i, i + size));
    }
    return chunks;
};

// Create a global _ object for backward compatibility
const _ = {
    map,
    each,
    delay,
    debounce,
    throttle,
    pluck,
    findWhere,
    without,
    chunk
};

// Export the global _ object for use in the browser
if (typeof window !== 'undefined') {
    window._ = _;
}

export default _;
